// src/pages/ActiveDesktop.jsx
import React, { useState, useEffect } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Settings, Menu, X } from 'lucide-react';

// Widget imports
import QuickStats from '../widgets/QuickStats';
import InventoryChart from '../widgets/InventoryChart';
import QuickLinks from '../widgets/QuickLinks';
import RecentMoves from '../widgets/RecentMoves';
import AgingReport from '../widgets/AgingReport';     // fixed typo
import AnomaliesWidget from '../widgets/AnomaliesWidget';
import NotesSummary from '../widgets/NotesSummary';

import '../App.css';
import './ActiveDesktop.css';  // ← new file, see below

// All available widgets with their default layout info
const AVAILABLE_WIDGETS = [
  { id: 'stats', name: 'Quick Stats', component: QuickStats, default: { x: 0, y: 0, w: 6, h: 3, minW: 4, minH: 2 } },
  { id: 'chart', name: 'Inventory Chart', component: InventoryChart, default: { x: 6, y: 0, w: 10, h: 5, minW: 6, minH: 4 } },
  { id: 'quicklinks', name: 'Quick Links', component: QuickLinks, default: { x: 0, y: 3, w: 6, h: 4, minW: 4, minH: 3 } },
  { id: 'recent', name: 'Recent Moves', component: RecentMoves, default: { x: 0, y: 7, w: 8, h: 5, minW: 6, minH: 4 } },
  { id: 'aging', name: 'Aging Report', component: AgingReport, default: { x: 8, y: 5, w: 8, h: 5, minW: 6, minH: 4 } },
  { id: 'anomalies', name: 'Anomalies', component: AnomaliesWidget, default: { x: 16, y: 0, w: 8, h: 5, minW: 6, minH: 4 } },
  { id: 'notes', name: 'Notes Summary', component: NotesSummary, default: { x: 0, y: 12, w: 6, h: 4, minW: 4, minH: 3 } },
];

const DEFAULT_LAYOUT = AVAILABLE_WIDGETS.map(w => ({
  i: w.id,
  ...w.default,
}));

export default function ActiveDesktop() {
  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem('active-desktop-layout');
    return saved ? JSON.parse(saved) : DEFAULT_LAYOUT;
  });

  const [enabledWidgets, setEnabledWidgets] = useState(() => {
    const saved = localStorage.getItem('active-desktop-enabled');
    return saved ? new Set(JSON.parse(saved)) : new Set(AVAILABLE_WIDGETS.map(w => w.id));
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Save layout
  useEffect(() => {
    localStorage.setItem('active-desktop-layout', JSON.stringify(layout));
  }, [layout]);

  // Save enabled widgets
  useEffect(() => {
    localStorage.setItem('active-desktop-enabled', JSON.stringify([...enabledWidgets]));
  }, [enabledWidgets]);

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const toggleWidget = (widgetId) => {
    setEnabledWidgets(prev => {
      const next = new Set(prev);
      if (next.has(widgetId)) {
        next.delete(widgetId);
      } else {
        next.add(widgetId);
      }
      return next;
    });
  };

  // Current active layout items (only enabled ones)
  const activeLayout = layout.filter(item => enabledWidgets.has(item.i));

  return (
    <div className="active-desktop-container">
      {/* Sidebar Toggle Button */}
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title={sidebarOpen ? "Close menu" : "Open widget menu"}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Left Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <h3>Widgets</h3>
        <ul className="widget-list">
          {AVAILABLE_WIDGETS.map(widget => (
            <li key={widget.id}>
              <label className="widget-toggle-label">
                <input
                  type="checkbox"
                  checked={enabledWidgets.has(widget.id)}
                  onChange={() => toggleWidget(widget.id)}
                />
                {widget.name}
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Dashboard */}
      <div className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>Active Desktop</h1>
          <button className="btn" onClick={() => window.location.assign('/pdi')}>
            Start PDI (In Stock - Calgary)
          </button>
        </div>

        <GridLayout
          className="layout"
          layout={activeLayout}
          cols={24}                  // increased from 12 → more flexibility
          rowHeight={80}             // slightly smaller rows = smoother feel
          width={window.innerWidth - (sidebarOpen ? 280 : 64)} // adjust for sidebar
          onLayoutChange={onLayoutChange}
          isDraggable={true}
          isResizable={true}
          compactType="vertical"
          preventCollision={false}
          margin={[16, 16]}
          useCSSTransforms={true}
        >
          {activeLayout.map(item => {
            const widget = AVAILABLE_WIDGETS.find(w => w.id === item.i);
            if (!widget) return null;

            const Comp = widget.component;

            return (
              <div key={item.i} className="widget-wrapper">
                <div className="widget-header">
                  <span className="widget-title-text">{widget.name}</span>
                  <button
                    className="widget-settings-btn"
                    title="Widget settings (coming soon)"
                    onClick={() => alert(`Settings for ${widget.name} - to be implemented`)}
                  >
                    <Settings size={18} />
                  </button>
                </div>
                <div className="widget-content">
                  <Comp />
                </div>
              </div>
            );
          })}
        </GridLayout>
      </div>
    </div>
  );
}
